import { useEffect, useState } from 'react';
import { Modal, Form, Button } from 'antd';

// Add/Edit/View modal owned by ErpDataGrid when the caller provides
// `renderForm` + `onSubmit`. View mode renders the same form with
// `disabled={true}` so consumers don't need a separate read-only view.
//
// The form's initialValues are recomputed every time the modal opens
// so an Edit → Cancel → Edit-different-row sequence never leaks the
// previous record's values into the next render.

const DEFAULT_TITLES = {
  add: 'Add record',
  edit: 'Edit record',
  view: 'View record'
};

export default function ErpDataGridFormModal({
  open,
  mode,
  record,
  renderForm,
  formInitialValues,
  formTitles,
  width,
  onSubmit,
  onCancel
}) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const titles = { ...DEFAULT_TITLES, ...(formTitles || {}) };
  const isView = mode === 'view';
  const isEdit = mode === 'edit';
  const isAdd  = mode === 'add';

  // Reset and prefill on every (re)open so leftover state from a
  // previous record never bleeds into the next instance. Two
  // deliberate choices here, both for open/close smoothness:
  //
  // 1. `formInitialValues` is NOT in the deps array. Consumers often
  //    pass an inline function recreated on every parent render —
  //    including it would re-fire this effect on every keystroke in
  //    the grid's search box, resetting the form mid-edit. The
  //    function ref is read fresh through closure on each open.
  //
  // 2. The form mutations (`resetFields` / `setFieldsValue`) are
  //    deferred one animation frame so the modal can start its
  //    open transition before we touch ~6+ AntD field instances.
  //    Doing this work synchronously during the open effect
  //    competed with the mask fade + content scale animations and
  //    produced a visible stall mid-open on Windows.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => {
      form.resetFields();
      if (isAdd) return;
      const initial =
        typeof formInitialValues === 'function'
          ? formInitialValues(record)
          : record || {};
      form.setFieldsValue(initial);
    });
    return () => cancelAnimationFrame(raf);
  }, [open, mode, record, form, isAdd]);

  async function onFinish(values) {
    if (isView) return;
    setSubmitting(true);
    try {
      await onSubmit(values);
      // parent closes modal on resolve via grid's handleFormSubmit
    } catch (_err) {
      // Submission errors are the caller's responsibility (notifyError
      // etc.); leave the modal open so the user can correct + retry.
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      title={titles[mode] || 'Record'}
      width={width}
      onCancel={onCancel}
      // destroyOnHidden intentionally OFF — destroying the entire
      // AntD Form tree on every close (and re-mounting it on every
      // open) caused a noticeable mid-animation stall. The form
      // persists between opens; the [open, mode, record] effect
      // above + `preserve={false}` on the Form handle isolation
      // between records.
      maskClosable={!submitting}
      className="erp-grid-form-modal"
      footer={
        isView ? (
          <Button size="small" onClick={onCancel}>
            Close
          </Button>
        ) : (
          <>
            <Button size="small" onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
            <Button
              size="small"
              type="primary"
              loading={submitting}
              onClick={() => form.submit()}
              className="!bg-[#1877F2] hover:!bg-blue-700 !border-0"
            >
              {isEdit ? 'Save changes' : 'Save'}
            </Button>
          </>
        )
      }
    >
      {/*
        size="small" sets compact heights on all AntD form children
        (Input, Select, InputNumber, DatePicker, Switch, Button),
        and the `.erp-grid-form` scope from index.css tightens the
        Form.Item spacing + label rhythm to match the grid density.
        Tabular-numeral feature-settings live in the same scope so
        numeric inputs line up cell-style with the table below.
      */}
      <Form
        form={form}
        layout="vertical"
        size="small"
        disabled={submitting || isView}
        onFinish={onFinish}
        preserve={false}
        className="erp-grid-form"
      >
        {renderForm({ mode, record, form, isView, isEdit, isAdd })}
      </Form>
    </Modal>
  );
}
